/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE BELOW-THRESHOLD COLLAPSE, AS DATA. ONE MECHANISM, TWO HOSTS. [UX-FINAL]
 *
 *  `TwoColumnLayout`'s secondary column and `AppShell`'s right rail had the same hole:
 *  below their split the secondary content either sat under a viewport cap while stacked
 *  full-width (the 880–919 band) or, in the rail's case, vanished outright
 *  (`hidden … xl:block`). CHANGELOG [P4-UX] known gap 3 recorded the only safe shape after
 *  two others were tried, and this is it:
 *
 *    ONE DOM COPY · a state-toggled class · no `matchMedia` · no width-branching render.
 *
 *  ⚠️ WHY NOT `ui/sheet.tsx`. Radix UNMOUNTS its content while closed — that file's header
 *  says so, and it is load-bearing there. Here the "closed" content is the intake
 *  checklist and the requirement preview, which roughly forty assertions address by test
 *  id without ever clicking anything open. Unmounting deletes them. A Sheet would also add
 *  a `<Sheet` site that `e2e/specs/census.spec.ts` fails on.
 *
 *  ⚠️ WHY A CLASS AND NEVER THE `hidden` ATTRIBUTE, `aria-hidden` OR `inert`.
 *  CLAUDE.md §8: jsdom loads no stylesheet, so a Tailwind `hidden` leaves the body fully
 *  addressable under test while a browser removes it from layout, tab order and the
 *  accessibility tree. The other three are honoured by jsdom and would delete the same
 *  forty assertions a width branch would.
 *
 *  ⚠️ EVERY THRESHOLD IS WRITTEN WITH THE SAME NUMBER ON BOTH SIDES.
 *  MEASURED in `tailwindcss@4.3.3/dist/lib.mjs`: `@max-` at the split emits `@container (width < N)`
 *  and `@min-` at the split emits `@container (width >= N)` — exact complements. The shape recorded
 *  in the CHANGELOG said `@max-[919px]:` beside a 920 split, which leaves 919 ≤ w < 920
 *  covered by neither, and container widths ARE fractional.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type CollapsePlacement = {
  /**
   * The panel's own box.
   *
   * ⚠️ NOT the sticky. MEASURED (a Chromium probe, both `display` modes plus a control):
   * a sticky box travels within its containing block, so a panel that positioned ITSELF
   * would be confined to a parent exactly its own height and could not move at all. The
   * position therefore lives on the box the HOST gives the panel — the grid slot for
   * `TwoColumnLayout`, nothing at all for the rail, which is already a flex child on the
   * fold.
   */
  readonly root: string;
  /** The toggle. Carries `@min-`…`:hidden` — above the split there is nothing to disclose. */
  readonly bar: string;
  /** The body, at every width: the height cap and the scroll on each side of the split. */
  readonly body: string;
  /** The body, only while collapsed. MUST be an `@max-` variant so it is inert above the split. */
  readonly collapsed: string;
};

/**
 * The shell rail's placement.
 *
 * Measured on `@container/shell` — the shell ROW — for the reason `layout.ts`'s
 * `RAIL_THRESHOLD_PX` states: the space beside `<main>` is `window − sidebar`, and the
 * sidebar's 256↔56 collapse moves it by 200px, which `xl:` could not see.
 *
 * ⚠️ NO STICKY HERE, DELIBERATELY. Below the threshold the shell row is `flex-col`, so the
 * rail is already the last child of a full-height flex column and sits on the fold by
 * construction. `position` would be answering a question the flex box has already answered.
 *
 * ⚠️ 1304 IS WRITTEN OUT because Tailwind scans source text and cannot read a constant.
 * `layout.test.ts` asserts the two agree, which is the guard that makes the literal safe.
 */
export const RAIL_COLLAPSE: CollapsePlacement = {
  root: 'min-h-0',
  bar: 'border-t @min-[1304px]/shell:hidden',
  body: 'min-w-0 overflow-y-auto @max-[1304px]/shell:max-h-[50dvh] @min-[1304px]/shell:max-h-full',
  collapsed: '@max-[1304px]/shell:hidden',
};
